import { StylesDerivedNativeActionNames } from '@wix/editor-platform-host-integration-apis';
import { GlobalDesignStyleItem, StylesGroup } from '../../types';
import { CompRef, StyleVariableValueValue } from '@wix/document-services-types';
import { EditorPointer } from '@wix/editorx-core-api';
import { MergingStrategyConfig, compareValuesPair } from './types';

const checkIfGroupsIsASubset = <
  TActionName extends StylesDerivedNativeActionNames,
>(
  leadingGroup: StylesGroup<TActionName>,
  subsetGroup: StylesGroup<TActionName>,
  allowSubsets: boolean,
): boolean => {
  const leadingItems = leadingGroup.items.reduce((acc, item) => {
    if (!acc[item.styleItem.cssPropertyType]) {
      acc[item.styleItem.cssPropertyType] = 1;
    } else {
      acc[item.styleItem.cssPropertyType]++;
    }
    return acc;
  }, {} as Record<string, number>);
  const subsetItems = subsetGroup.items.reduce((acc, item) => {
    if (!acc[item.styleItem.cssPropertyType]) {
      acc[item.styleItem.cssPropertyType] = 1;
    } else {
      acc[item.styleItem.cssPropertyType]++;
    }
    return acc;
  }, {} as Record<string, number>);
  const isLengthEqual = subsetGroup.items.length === leadingGroup.items.length;

  if (!isLengthEqual && !allowSubsets) {
    return false;
  }

  return Object.keys(subsetItems).every(
    (item) => leadingItems[item] >= subsetItems[item],
  );
};

const compareGroupPair = async <
  TActionName extends StylesDerivedNativeActionNames,
>(
  leadingGroup: StylesGroup<TActionName>,
  subsetGroup: StylesGroup<TActionName>,
  config: MergingStrategyConfig<TActionName>,
): Promise<Map<
  string,
  {
    styleItemId: string;
    styleItem: GlobalDesignStyleItem<TActionName>;
    editorPointer: EditorPointer<CompRef>;
  }
> | null> => {
  const leadingQuery = leadingGroup.context.query;
  const subsetQuery = subsetGroup.context.query;

  // TODO: fix types and remove type assertions
  const leadingStyles = (await leadingQuery.getStyles())?.cssProperties;
  const subsetStyles = (await subsetQuery.getStyles())?.cssProperties;

  const availableLeadingItems = [...leadingGroup.items];
  const matchingMap = new Map<
    string,
    {
      styleItemId: string;
      styleItem: GlobalDesignStyleItem<TActionName>;
      editorPointer: EditorPointer<CompRef>;
    }
  >();

  for (const subsetItemWrapper of subsetGroup.items) {
    const subsetItem = subsetItemWrapper.styleItem;
    const subsetValue = subsetStyles[subsetItemWrapper.styleItemId];

    let foundMatch = false;
    for (let i = 0; i < availableLeadingItems.length; i++) {
      const leadingItemWrapper = availableLeadingItems[i];
      const leadingItem = leadingItemWrapper.styleItem;

      if (leadingItem.cssPropertyType === subsetItem.cssPropertyType) {
        const leadingValue = leadingStyles[leadingItemWrapper.styleItemId];

        if (
          compareValuesPair(
            subsetItem.cssPropertyType,
            subsetValue,
            leadingValue,
            config,
          )
        ) {
          matchingMap.set(leadingItemWrapper.styleItemId, {
            ...subsetItemWrapper,
            editorPointer: subsetGroup.editorPointer,
          });
          availableLeadingItems.splice(i, 1);
          foundMatch = true;
          break;
        }
      }
    }

    if (!foundMatch) {
      return null;
    }
  }

  return matchingMap;
};

const defaultStrategy = async <
  TActionName extends StylesDerivedNativeActionNames,
>(
  stylesGroups: StylesGroup<TActionName>[],
  config: MergingStrategyConfig<TActionName>,
): Promise<StylesGroup<TActionName>[]> => {
  const processedGroups = new Set<StylesGroup<TActionName>>();
  const linkedGroups: StylesGroup<TActionName>[] = [];

  for (const group of stylesGroups) {
    if (processedGroups.has(group)) {
      continue;
    }
    processedGroups.add(group);

    const subsetGroups = await Promise.all(
      stylesGroups.map(async (g) => {
        if (
          processedGroups.has(g) ||
          !checkIfGroupsIsASubset(group, g, config.allowSubsets)
        ) {
          return null;
        }
        const result = await compareGroupPair(group, g, config);
        if (result) {
          processedGroups.add(g);
        }
        return result;
      }),
    ).then((results) => {
      return results.filter((result) => result !== null);
    });

    linkedGroups.push({
      ...group,
      items: group.items.map((leadingGroupItem) => {
        const linkedStyleItems = subsetGroups.map((subsetGroup) => {
          const subsetItem = subsetGroup.get(leadingGroupItem.styleItemId);

          if (!subsetItem) {
            return null;
          }

          return {
            editorPointer: subsetItem.editorPointer,
            styleItem: subsetItem.styleItem,
            styleItemId: subsetItem.styleItemId,
          };
        });

        return {
          ...leadingGroupItem,
          linkedStyleItems: linkedStyleItems.filter((item) => item !== null),
        };
      }),
    });
  }
  return linkedGroups;
};

export default defaultStrategy;
